import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { queryKeys } from '~/api/queryKeys';

import {
  fetchCostApprovalSettings,
  fetchCostApprovalSettingsHistory,
  updateCostApprovalSettings,
  type CostApprovalWriteActor,
} from '../services/cost-approval-settings.service';
import type { CostApprovalSettingsValues } from '../types/cost-approval-settings.types';

/** Loads the global cost-approval gate settings; `enabled` off skips the request (customers).
 *  Never retried: the cost display gate hides amounts until this settles, and a failed read
 *  must fall back to "shown" at once rather than after the 5xx retries. */
export function useCostApprovalSettings(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queryKeys.costApprovalSettings.detail(),
    queryFn: fetchCostApprovalSettings,
    enabled: options?.enabled ?? true,
    staleTime: 30 * 1000,
    retry: false,
  });
}

/** One page (1-based) of the settings change history; keeps the previous page while paging. */
export function useCostApprovalSettingsHistory(
  page: number,
  pageSize: number,
  options?: { enabled?: boolean },
) {
  const params = { limit: pageSize, offset: (page - 1) * pageSize };
  return useQuery({
    queryKey: queryKeys.costApprovalSettings.history(params),
    queryFn: () => fetchCostApprovalSettingsHistory(params),
    enabled: options?.enabled ?? true,
    placeholderData: (prev) => prev,
    staleTime: 30 * 1000,
  });
}

/** Replaces the settings; the saved row is written straight into the cache and the history refetched. */
export function useUpdateCostApprovalSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      values,
      actor,
    }: {
      values: CostApprovalSettingsValues;
      actor: CostApprovalWriteActor;
    }) => updateCostApprovalSettings(values, actor),
    onSuccess: (saved) => {
      qc.setQueryData(queryKeys.costApprovalSettings.detail(), saved);
      void qc.invalidateQueries({ queryKey: [...queryKeys.costApprovalSettings.all, 'history'] });
    },
  });
}
